/**
 * @file Service de sécurité côté client.
 * @description
 * Ce service fournit un ensemble d'utilitaires visant à renforcer la sécurité
 * de l'application côté client. Il ne remplace pas les validations et protections
 * côté serveur, mais agit comme une première ligne de défense.
 *
 * Fonctionnalités :
 * - **Sanitization:** Nettoyage basique des entrées pour se prémunir contre les
 *   attaques XSS.
 * - **Validation:** Fonctions de validation strictes pour les formats de données
 *   courants comme les emails.
 * - **Détection de Patterns:** Logique simple pour détecter des tentatives
 *   d'injection SQL basiques.
 * - **Rate Limiting Côté Client:** Un mécanisme simple pour prévenir le "spam"
 *   de soumissions de formulaires.
 * - **Gestion CSRF (placeholder):** Logique pour extraire et ajouter des
 *   tokens CSRF, si le backend en implémente.
 * - **Logging Sécurisé:** Une classe `SecureLogger` pour logger des informations
 *   en production tout en s'assurant de ne pas exposer de données sensibles.
 */

export class SecurityService {
  private static csrfToken: string | null = null;

  /**
   * Récupère le token CSRF depuis les headers de réponse
   */
  static extractCSRFToken(response: Response): void {
    const token = response.headers.get("X-CSRF-Token");
    if (token) {
      this.csrfToken = token;
      console.warn(
        "🔒 Token CSRF extrait et stocké:",
        token.substring(0, 20) + "...",
      );
    }
  }

  /**
   * Récupère le token CSRF depuis les cookies du navigateur
   */
  static extractCSRFTokenFromCookies(): void {
    try {
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "X-CSRF-TOKEN") {
          this.csrfToken = decodeURIComponent(value);
          console.warn(
            "🔒 Token CSRF extrait des cookies:",
            this.csrfToken.substring(0, 20) + "...",
          );
          break;
        }
      }
    } catch (error) {
      console.warn(
        "Erreur lors de l'extraction du token CSRF depuis les cookies:",
        error,
      );
    }
  }

  /**
   * Ajoute le token CSRF aux headers de requête
   */
  static getCSRFHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.csrfToken) {
      headers["X-CSRF-Token"] = this.csrfToken;
    }
    return headers;
  }

  /**
   * Sanitise les entrées utilisateur contre XSS
   */
  static sanitizeInput(input: string): string {
    if (!input) return input;

    // Échapper les caractères HTML dangereux
    return input
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#x27;")
      .replace(/\//g, "&#x2F;");
  }

  /**
   * Valide un email de manière sécurisée
   */
  static validateEmail(email: string): boolean {
    if (!email || email.length > 320) return false; // RFC 5321 limite

    // Regex stricte pour email
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    // Additional validation for common invalid patterns
    if (email.includes('@@') || email.includes('..')) {
      return false;
    }

    if (!emailRegex.test(email)) return false;

    // Vérifications supplémentaires contre les injections
    const dangerousPatterns = [
      /<script/i,
      /javascript:/i,
      /onload=/i,
      /onerror=/i,
      /[\r\n]/, // Pas de retours à la ligne (header injection)
      /%0a|%0d/i, // Encoded line breaks
    ];

    return !dangerousPatterns.some((pattern) => pattern.test(email));
  }

  /**
   * Valide les données de formulaire
   */
  static validateFormData(data: Record<string, unknown>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Vérifier les champs requis
    if (!data.firstName || typeof data.firstName !== "string") {
      errors.push("Prénom requis");
    } else if (data.firstName.length > 100) {
      errors.push("Prénom trop long (max 100 caractères)");
    }

    if (!data.lastName || typeof data.lastName !== "string") {
      errors.push("Nom requis");
    } else if (data.lastName.length > 100) {
      errors.push("Nom trop long (max 100 caractères)");
    }

    if (!data.email || typeof data.email !== "string" || !this.validateEmail(data.email)) {
      errors.push("Email invalide");
    }

    // Vérifier les limites de taille
    if (data.notes && typeof data.notes === "string" && data.notes.length > 1000) {
      errors.push("Notes trop longues (max 1000 caractères)");
    }

    // Vérifier les injections potentielles
    const textFields = ["firstName", "lastName", "notes"];
    for (const field of textFields) {
      const fieldValue = data[field];
      if (fieldValue && typeof fieldValue === "string" && this.detectSQLInjection(fieldValue)) {
        errors.push(`Contenu suspect détecté dans ${field}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Détecte les tentatives d'injection SQL basiques
   */
  private static detectSQLInjection(input: string): boolean {
    const sqlPatterns = [
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION)\b)/i,
      /(\b(OR|AND)\s+\d+\s*=\s*\d+)/i,
      /(--|#|\/\*|\*\/)/,
      /('|";?\s*(OR|AND|SELECT|INSERT|UPDATE|DELETE))/i,
    ];

    return sqlPatterns.some((pattern) => pattern.test(input));
  }

  /**
   * Génère un nonce pour CSP
   */
  static generateNonce(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
      "",
    );
  }

  /**
   * Valide que les prix n'ont pas été manipulés côté client
   */
  static validatePriceIntegrity(
    selectedSpots: unknown[],
    expectedTotalPrice: number,
    tolerance: number = 0.01,
  ): boolean {
    // Recalculer le prix côté client
    const calculatedPrice = selectedSpots.reduce((total: number, spot: unknown) => {
      const pricePerDay = (spot as { price_per_day?: number })?.price_per_day ?? 0;
      return total + pricePerDay;
    }, 0);

    // Vérifier avec une tolérance
    const difference = Math.abs(calculatedPrice - expectedTotalPrice);

    if (difference > tolerance) {
      console.warn("⚠️ Incohérence de prix détectée:", {
        calculated: calculatedPrice,
        expected: expectedTotalPrice,
        difference,
      });
      return false;
    }

    return true;
  }

  /**
   * Limite le taux de soumission (rate limiting côté client)
   */
  private static lastSubmission: number = 0;
  private static submissionCount: number = 0;
  private static readonly SUBMISSION_WINDOW = 60000; // 1 minute
  private static readonly MAX_SUBMISSIONS = 3; // 3 soumissions par minute

  static canSubmit(): boolean {
    const now = Date.now();

    // Reset counter si la fenêtre est expirée
    if (now - this.lastSubmission > this.SUBMISSION_WINDOW) {
      this.submissionCount = 0;
    }

    // Vérifier la limite
    if (this.submissionCount >= this.MAX_SUBMISSIONS) {
      return false;
    }

    // Enregistrer la soumission
    this.submissionCount++;
    this.lastSubmission = now;

    return true;
  }

  /**
   * Nettoie les données sensibles du localStorage
   */
  static clearSensitiveData(): void {
    // Ne pas stocker de données sensibles localement
    const keysToRemove = [
      "payment_info",
      "credit_card",
      "personal_data",
      "csrf_token",
    ];

    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove ${key} from localStorage:`, error);
      }
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove ${key} from sessionStorage:`, error);
      }
    });
  }

  /**
   * Échappe les caractères pour prévenir les injections dans les URLs
   */
  static escapeForURL(value: string): string {
    return encodeURIComponent(value);
  }

  /**
   * Valide que les dates sont sécurisées et cohérentes
   */
  static validateDates(
    startDate: Date,
    endDate: Date,
  ): {
    isValid: boolean;
    error?: string;
  } {
    const now = new Date();
    // Une arrivée le jour même est autorisée (réservation d'un pêcheur qui se
    // présente aujourd'hui). On compare donc au DÉBUT de la journée courante,
    // pas à l'instant présent : minuit aujourd'hui n'est pas « dans le passé ».
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const maxFutureDate = new Date();
    maxFutureDate.setFullYear(now.getFullYear() + 2); // Max 2 ans dans le futur

    if (startDate < startOfToday) {
      return {
        isValid: false,
        error: "Date de début ne peut pas être dans le passé",
      };
    }

    if (endDate <= startDate) {
      return {
        isValid: false,
        error: "Date de fin doit être après la date de début",
      };
    }

    if (startDate > maxFutureDate) {
      return { isValid: false, error: "Date trop loin dans le futur" };
    }

    const daysDifference =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysDifference > 30) {
      return { isValid: false, error: "Durée maximale de 30 jours" };
    }

    return { isValid: true };
  }
}

/**
 * Utilitaires de logging sécurisé
 */
export class SecureLogger {
  /**
   * Log sans exposer d'informations sensibles
   */
  static logSecure(
    level: "info" | "warn" | "error",
    message: string,
    data?: unknown,
  ): void {
    const sanitizedData = data ? this.sanitizeLogData(data) : undefined;

    switch (level) {
      case "info":
        console.warn(`🔒 [SECURE] ${message}`, sanitizedData);
        break;
      case "warn":
        console.warn(`⚠️ [SECURITY] ${message}`, sanitizedData);
        break;
      case "error":
        console.error(`🚨 [SECURITY ERROR] ${message}`, sanitizedData);
        break;
    }
  }

  /**
   * Retire les données sensibles des logs
   */
  private static sanitizeLogData(data: unknown): unknown {
    if (typeof data !== "object" || data === null) {
      return data;
    }

    const sensitiveKeys = [
      "password",
      "token",
      "csrf",
      "credit_card",
      "cvv",
      "social_security",
      "passport",
      "email",
      "phone",
      "number",
    ];

    const sanitized: Record<string, unknown> = Array.isArray(data)
      ? [...data] as unknown as Record<string, unknown>
      : { ...(data as Record<string, unknown>) };

    for (const key in sanitized) {
      if (
        sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))
      ) {
        sanitized[key] = "[REDACTED]";
      } else if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
        // Recursively sanitize nested objects
        sanitized[key] = this.sanitizeLogData(sanitized[key]);
      }
    }

    return sanitized;
  }
}
